import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { BotanicalCorner, GoldLineDivider, VerticalAccentLine } from '@/components/feature/DrawingLines';

const values = [
  {
    icon: 'ri-shield-check-line',
    title: 'Medical Grade\n위생 시스템',
    description:
      '모든 시술 도구는 1회용 멸균 키트를 사용하며, WHO 기준을 초과하는 자체 위생 프로토콜을 운영합니다. 고객의 안전이 예술보다 먼저입니다.',
    tag: 'Safety First',
    accent: '#D1D1D1',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: '체계적인\n아카데미 교육',
    description:
      '6개월 집중 교육 과정과 현장 실습을 통해 아티스트를 양성합니다. 이론부터 실전까지, 선율만의 커리큘럼으로 최고의 전문가를 만듭니다.',
    tag: 'Education',
    accent: '#D1D1D1',
  },
  {
    icon: 'ri-line-chart-line',
    title: '검증된\n수익 모델',
    description:
      '평균 월 매출 3,200만원, 가맹점 만족도 97%. 본사의 마케팅 지원과 공동 구매 시스템으로 안정적인 수익을 보장합니다.',
    tag: 'Revenue',
    accent: '#D1D1D1',
  },
];

export default function ValuesSection() {
  const { ref, visible } = useScrollAnimation(0.15);

  return (
    <section className="py-28 md:py-36 bg-white overflow-hidden relative" ref={ref}>
      {/* Corner botanical ornaments */}
      <BotanicalCorner position="top-right" color="rgba(201,169,110,0.12)" size={180} />
      <BotanicalCorner position="bottom-left" color="rgba(201,169,110,0.10)" size={150} />
      {/* Vertical accent lines */}
      <VerticalAccentLine side="left" color="#C9A96E" height={180} />
      <VerticalAccentLine side="right" color="#C9A96E" height={180} />

      <div className="max-w-[1440px] mx-auto px-5 md:px-16">
        {/* Title */}
        <div
          className="text-center mb-10 transition-all duration-1000 ease-out"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(30px)',
          }}
        >
          <p className="text-xs tracking-[0.3em] uppercase text-[#999] mb-4">Why 선율</p>
          <h2 className="text-2xl md:text-4xl font-thin text-[#1A1A1A] tracking-tight">Our Standards</h2>
        </div>

        {/* Gold line divider */}
        <div className="mb-14">
          <GoldLineDivider delay={0.3} />
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-8">
          {values.map((v, i) => (
            <div
              key={v.title}
              className="group relative border border-[#E5E5E5] p-7 md:p-10 lg:p-12 cursor-default overflow-hidden transition-all duration-700 ease-out hover:border-[#1A1A1A]"
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? 'translateY(0)' : 'translateY(50px)',
                transitionDelay: `${i * 150}ms`,
              }}
            >
              {/* Hover fill bg */}
              <div className="absolute inset-0 bg-[#1A1A1A] transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out" />

              {/* Number */}
              <span
                className="absolute top-8 right-10 text-6xl font-thin text-[#F2F2F2] group-hover:text-white/10 transition-colors duration-500 select-none"
                style={{ lineHeight: 1 }}
              >
                0{i + 1}
              </span>

              <div className="relative z-10">
                <div className="w-12 h-12 flex items-center justify-center mb-10">
                  <i className={`${v.icon} text-3xl text-[#1A1A1A] group-hover:text-white transition-colors duration-500`} />
                </div>
                <p className="text-xs tracking-[0.2em] uppercase text-[#AAAAAA] group-hover:text-white/50 mb-4 transition-colors duration-500">
                  {v.tag}
                </p>
                <h3 className="text-lg md:text-xl font-semibold text-[#1A1A1A] group-hover:text-white leading-snug mb-4 whitespace-pre-line transition-colors duration-500">
                  {v.title}
                </h3>
                <p className="text-sm text-[#666] group-hover:text-white/70 leading-relaxed transition-colors duration-500">
                  {v.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
